import React from "react";
import PropTypes from "prop-types";
import { Wrapper } from "./Wrapper";
import { ContentWrap } from "./ContentWrap";
import { RemoveOption } from "./RemoveOption";
import { GlossaryOption } from "./GlossaryOption";
import { Options } from "./Options";
import { GlossaryProvider } from "@learningblocks/storybook-components";
import { MoveOption } from "./MoveOption";
import { RenderHTML, Image } from "@learningblocks/shared-components";
import { Description } from "./Description";

import cn from "./style.module.scss";

/**
 * RectangleNode
 *  -
 *
 */

const COLOUR_NORMAL = "#f7f7f7";
const COLOR_HOVER = "#E5ECF0";

export function RectangleCard(props) {
  const { onOpenGlossary } = React.useContext(GlossaryProvider.Context);

  const [backgroundColor, setColor] = React.useState(COLOUR_NORMAL);

  //for detecting hover
  const onMouseLeave = () => {
    setColor(COLOUR_NORMAL);
  };
  const onMouseEnter = () => {
    setColor(COLOR_HOVER);
  };

  const searchGlossary = () => {
    onOpenGlossary(props.glossaryId);
  };
  const isGlossary = !props.addCard && props.glossaryId;

  const className = `${cn.rectangle} ${props.hasShadow ? cn.shadow : ""} ${
    isGlossary ? cn.glossaryCard : ""
  } ${props.className}`;

  const onClickIfNotOption = () => {
    if (!props.isMobileOption && !props.addCard && props.glossaryId)
      searchGlossary();
  };

  return (
    <Wrapper
      className={className}
      tabIndex={props.tabIndex}
      onClick={onClickIfNotOption}
    >
      <div
        className={`${cn.rectangle_imageWrap} ${
          isGlossary ? cn.glossaryCard_imageWrap : ""
        }`}
      >
        <Image
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          className={`${cn.rectangle_image} ${
            isGlossary ? cn.glossaryCard_image : ""
          }`}
          src={props.image}
          alt=""
        />
      </div>
      <ContentWrap
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className={`${cn.rectangle_content} ${
          isGlossary ? cn.glossaryCard_content : ""
        }`}
      >
        <RenderHTML content={props.title} />
        <Description description={props.description} trim={props.trim} />
      </ContentWrap>
      {isGlossary && (
        <div className={cn.glossaryCard_iconWrap}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M1.51499 0C1.04639 0 0.666504 0.3731 0.666504 0.833333V16.6667V18.3333C0.666504 19.2538 1.42626 20 2.36347 20H3.20906H3.21196H17.6362C18.5734 20 19.3332 19.2538 19.3332 18.3333V2.5C19.3332 2.03977 18.9533 1.66667 18.4847 1.66667H17.9998V17.6667C17.9998 18.3333 17.6362 18.6667 16.9998 18.6667H2.6665C2.33317 18.6667 1.99984 18.3333 1.99984 18C1.99984 17.6667 2.36347 17.3333 2.6665 17.3333H15.9998C16.4685 17.3333 16.6665 17.1269 16.6665 16.6667V1C16.6665 0.539767 16.1351 0 15.6665 0H1.51499ZM6.86832 12.4892H11.1188L11.935 14.9243H14.5128L10.1087 3.10605H7.86224L3.48247 14.9243H6.06024L6.86832 12.4892ZM10.4643 10.5168H7.52285L8.98547 6.09307L10.4643 10.5168Z"
              fill="#003E6B"
            ></path>
          </svg>
        </div>
      )}
      {props.removeOption || isGlossary ? (
        <></>
      ) : (
        <Options className={`${cn.rectangle_option}`}>
          <GlossaryOption
            className={cn.rectangle_option_iconWrap}
            onKeyDown={props.openGlossary}
            onClick={searchGlossary}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            isOption={props.addCard}
            style={{ backgroundColor: backgroundColor }}
            tabIndex={props.tabIndex}
            title={props.title}
          />
          <RemoveOption
            className={cn.rectangle_option_iconWrap}
            onSelect={props.removeCard}
            tabIndex={props.tabIndex}
          />
          <MoveOption
            className={cn.rectangle_option_iconWrap}
            onSelect={props.addCard}
            tabIndex={props.tabIndex}
          />
        </Options>
      )}
    </Wrapper>
  );
}

RectangleCard.propTypes = {
  /**
   * tabIndex to set on component
   */
  tabIndex: PropTypes.number,
  /**
   * ClassName to set on component
   */
  className: PropTypes.string,
  /**
   * callback function to be executed when card is added as next step in DesignSection
   * Prop should be null if addCard icon not required
   */
  addCard: PropTypes.func,
  /**
   * callback function to be executed when card needs to be removed from design section tree
   * Prop should be null if removeCard icon not required
   */
  removeCard: PropTypes.func,
  /**
   * Callback function to be executed when info icon in the card is clicked or selected
   * the callback use the title of card to invoke a search operation in glossary
   */
  openGlossary: PropTypes.func,
  /**
   * Url to the small card icon image
   */
  image: PropTypes.string.isRequired,
  /**
   * Title of the card
   */
  title: PropTypes.string.isRequired,
  /**
   * display shadow box
   */
  hasShadow: PropTypes.bool,
  /**
   * description for rectangle card
   */
  description: PropTypes.string,
  /**
   * spcifies if it is used as option in mob/tab
   */
  isMobileOption: PropTypes.bool,
  /**
   * Trim is a boolean used to ditermine wheather the description should trim or not
   */
  trim: PropTypes.bool,
  /**
   * remove option hides the option on the card
   */
  removeOption: PropTypes.bool,
};

RectangleCard.defaultProps = {
  tabIndex: 0,
  removeCard: undefined,
  hasShadow: true,
  isMobileOption: false,
  trim: true,
};
